/* eslint-disable @typescript-eslint/no-explicit-any */
import { shouldSendRemoteTelemetry } from "@utils/envs";
import { getClient, sentryLogger } from "@utils/sentry";

type LogLevel = "debug" | "info" | "warn" | "error" | "fatal";
type LogArea =
  | "updates"
  | "player"
  | "network"
  | "db"
  | "auth"
  | "audio"
  | "app";
type LogAttributes = Record<string, unknown>;

const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  debug: console.debug,
  info: console.info,
};

const consoleAreas = ["player", "network", "db", "auth", "audio"] as const;
let isLogging = false;

const isPlainObject = (value: unknown): value is LogAttributes =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  !(value instanceof Error);

const normalizeValue = (value: unknown): unknown => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  return value;
};

const normalizeAttributes = (attributes?: LogAttributes): LogAttributes => {
  if (!attributes) return {};

  return Object.fromEntries(
    Object.entries(attributes).map(([key, value]) => [
      key,
      normalizeValue(value),
    ]),
  );
};

const normalizeConsoleArgs = (
  args: unknown[],
): { event: string; attributes?: LogAttributes } => {
  const [firstArg, ...rest] = args;
  const event =
    typeof firstArg === "string"
      ? firstArg
      : JSON.stringify(normalizeValue(firstArg));

  if (rest.length === 0) {
    return { event };
  }

  if (rest.length === 1 && isPlainObject(rest[0])) {
    return { event, attributes: rest[0] };
  }

  return {
    event,
    attributes: {
      args: rest.map(normalizeValue),
    },
  };
};

const writeLocalLog = (
  level: Exclude<LogLevel, "fatal">,
  area: LogArea,
  event: string,
  attributes?: LogAttributes,
) => {
  const method =
    level === "debug"
      ? "debug"
      : level === "warn"
        ? "warn"
        : level === "error"
          ? "error"
          : "info";

  if (attributes && Object.keys(attributes).length > 0) {
    originalConsole[method](`[${area}] ${event}`, attributes);
    return;
  }

  originalConsole[method](`[${area}] ${event}`);
};

const captureStructuredLog = (
  level: LogLevel,
  area: LogArea,
  event: string,
  attributes?: LogAttributes,
) => {
  const normalizedAttributes = {
    area,
    ...normalizeAttributes(attributes),
  };

  if (isLogging) {
    writeLocalLog(
      level === "fatal" ? "error" : level,
      area,
      event,
      normalizedAttributes,
    );
    return;
  }

  isLogging = true;
  try {
    if (shouldSendRemoteTelemetry() && getClient()) {
      sentryLogger[level](event, normalizedAttributes);
      return;
    }

    writeLocalLog(
      level === "fatal" ? "error" : level,
      area,
      event,
      normalizedAttributes,
    );
  } finally {
    isLogging = false;
  }
};

export const logger = {
  debug: (area: LogArea, event: string, attributes?: LogAttributes) =>
    captureStructuredLog("debug", area, event, attributes),
  info: (area: LogArea, event: string, attributes?: LogAttributes) =>
    captureStructuredLog("info", area, event, attributes),
  warn: (area: LogArea, event: string, attributes?: LogAttributes) =>
    captureStructuredLog("warn", area, event, attributes),
  error: (area: LogArea, event: string, attributes?: LogAttributes) =>
    captureStructuredLog("error", area, event, attributes),
  fatal: (area: LogArea, event: string, attributes?: LogAttributes) =>
    captureStructuredLog("fatal", area, event, attributes),
};

const createConsoleShim =
  (level: Exclude<LogLevel, "fatal">, area: LogArea) =>
  (...args: any[]) => {
    const { event, attributes } = normalizeConsoleArgs(args);
    captureStructuredLog(level, area, event, attributes);
  };

const createNamespacedConsoleLogger = (area: LogArea) => ({
  log: createConsoleShim("info", area),
  info: createConsoleShim("info", area),
  warn: createConsoleShim("warn", area),
  error: createConsoleShim("error", area),
  debug: createConsoleShim("debug", area),
});

export const setupLogger = () => {
  if ((console as any)._isPatched) return;

  global.console.log = createConsoleShim("info", "app");
  global.console.info = createConsoleShim("info", "app");
  global.console.warn = createConsoleShim("warn", "app");
  global.console.error = createConsoleShim("error", "app");
  global.console.debug = createConsoleShim("debug", "app");

  consoleAreas.forEach((area) => {
    (global.console as any)[area] = createNamespacedConsoleLogger(area);
  });

  (console as any)._isPatched = true;
  originalConsole.log("Logger initialized");
};

declare global {
  interface Console {
    _isPatched?: boolean;
    player: ConsoleLogger;
    network: ConsoleLogger;
    db: ConsoleLogger;
    auth: ConsoleLogger;
    audio: ConsoleLogger;
  }
}

interface ConsoleLogger {
  log: (...args: any[]) => void;
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
  debug: (...args: any[]) => void;
}
